"use client";

import { useEffect, useState } from "react";
import axios from "axios";
import ReactMarkdown, { type Components } from "react-markdown";
import AppHeader from "@/components/AppHeader";
import { getLegalDocument } from "@/utils/api";
import { type LegalSlug, slugTitleMap } from "./slugs";

type FetchState =
  | { status: "loading" }
  | { status: "ready"; body: string }
  | { status: "error"; message: string };

const isExternalHref = (href: string | undefined): boolean =>
  typeof href === "string" && /^https?:\/\//i.test(href);

const markdownComponents: Components = {
  h1: ({ ...props }) => <h2 {...props} />,
  h2: ({ ...props }) => <h3 {...props} />,
  h3: ({ ...props }) => <h4 {...props} />,
  h4: ({ ...props }) => <h5 {...props} />,
  h5: ({ ...props }) => <h6 {...props} />,
  a: ({ href, children, ...props }) =>
    isExternalHref(href) ? (
      <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
        {children}
      </a>
    ) : (
      <a href={href} {...props}>
        {children}
      </a>
    ),
};

export default function LegalPageContent({ slug }: { slug: LegalSlug }) {
  const [state, setState] = useState<FetchState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    getLegalDocument(slug)
      .then((res) => {
        if (cancelled) return;
        const raw = res.data?.body;
        const body = typeof raw === "string" ? raw : "";
        setState({ status: "ready", body });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const status = axios.isAxiosError(err) ? err.response?.status : undefined;
        const message =
          status === 404
            ? "This document is not available yet."
            : "Unable to load this document. Please try again.";
        setState({ status: "error", message });
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return (
    <main className="min-h-screen bg-white">
      <AppHeader title={slugTitleMap[slug]} showBackButton />
      <article
        aria-busy={state.status === "loading"}
        className="mx-auto max-w-2xl px-6 py-6 prose prose-sm sm:prose"
      >
        {state.status === "loading" && (
          <p role="status" aria-live="polite" className="text-sm text-gray-500">
            Loading document…
          </p>
        )}
        {state.status === "error" && (
          <p className="text-sm text-red-600" role="alert">
            {state.message}
          </p>
        )}
        {state.status === "ready" &&
          (state.body.trim().length > 0 ? (
            <ReactMarkdown components={markdownComponents}>{state.body}</ReactMarkdown>
          ) : (
            <p role="status" aria-live="polite" className="text-sm text-gray-500">
              Counsel-approved copy is in preparation. This document will be available shortly.
            </p>
          ))}
      </article>
    </main>
  );
}
